import React, { useState, useEffect } from 'react';
import { selectorPlanoSaude } from '@core/slice/beneficiosPlanoSaude.slice';
import { useAppSelector } from '@store/hooks';
import { Avatar, Box, Container, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { colaboradorService } from '@core/services';
import { formatDate, getDateFromSap, getInitials, isFilled, isObjectEmpty, formatCurrency, isDateInRange } from '@core/utils';
import { ContentSeparator, DatePicker, LoadingView } from '@core/components';
import { AlertComponent } from '@core/components/atoms/alert-component';
import { TableCellStyled } from '@core/components/organisms/simple-table/styled';

interface IBeneficiosPlanoSaudeProps {}
export const Saude: React.FC<IBeneficiosPlanoSaudeProps> = () => {
    const { data, loading, error } = useAppSelector(selectorPlanoSaude);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs().startOf('month'));

    useEffect(() => {
        colaboradorService.beneficiosPlanoSaude(dayjs(selectedDate).format('YYYY-MM-DD'));
    }, []);

    const handleDateChange = (newDate: Dayjs | null) => {
        setSelectedDate(newDate);
        colaboradorService.beneficiosPlanoSaude(dayjs(newDate).format('YYYY-MM-DD'));
    };

    const sharedFontSize = {
        fontSize: '0.8rem'
    };

    const sharedFontSizeBold = {
        fontSize: '0.8rem',
        fontWeight: 'bold'
    };

    return (
        <Container maxWidth="xl" sx={{ px: '2rem' }}>
            <Grid>
                <DatePicker selectedDate={selectedDate} onDateChange={handleDateChange} />
            </Grid>
            {loading && <LoadingView />}

            {isFilled(error) && !loading ? (
                <AlertComponent type="error" detailed={true} message={error ?? '-'} />
            ) : isObjectEmpty(data) && !loading ? (
                <AlertComponent type="info" detailed={true} message="Pesquise o demonstrativo." />
            ) : !loading ? (
                <>
                    {data.beneficiarios.map((beneficiário, index) => {
                        const mesInclusao = new Date(beneficiário.mesInclusao).getTime();
                        const currentDate = new Date(dayjs(selectedDate).format('YYYY-MM-DD')).getTime();

                        if (
                            isDateInRange(dayjs(selectedDate).format('YYYY-MM-DD'), dayjs(beneficiário.mesInclusao).format('YYYY-MM-DD'), dayjs(beneficiário.mesExclusao).format('YYYY-MM-DD')) ||
                            (mesInclusao <= currentDate && beneficiário.mesExclusao === '1900-12-31')
                        ) {
                            return (
                                <>
                                    <ContentSeparator title={'DADOS DO ' + beneficiário.tipoBeneficiario} />
                                    <Grid container spacing={2}>
                                        <Grid item md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                                            <IconButton sx={{ mr: '4px', p: 0 }}>
                                                <Avatar sx={{ bgcolor: '#FF00FF' }} alt={beneficiário.nomeBeneficiario || '-'}>
                                                    {getInitials(beneficiário.nomeBeneficiario) || '-'}
                                                </Avatar>
                                            </IconButton>
                                            <Grid>
                                                <Typography style={sharedFontSizeBold}>{beneficiário.nomeBeneficiario}</Typography>
                                                <Typography style={sharedFontSize}>{beneficiário.tipoBeneficiario}</Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid item md={3}>
                                            <Typography style={sharedFontSizeBold}>PLANO CONTRATADO</Typography>
                                            <Typography style={sharedFontSize}>{beneficiário.nomePlano}</Typography>
                                        </Grid>
                                        <Grid item md={3}>
                                            <Typography style={sharedFontSizeBold}>OPERADORA</Typography>
                                            <Typography style={sharedFontSize}>{beneficiário.operadora}</Typography>
                                        </Grid>
                                        <Grid item md={2}>
                                            <Typography style={sharedFontSizeBold}>VALOR</Typography>
                                            <Typography style={sharedFontSize}>{formatCurrency(Number(beneficiário.mensalidade ?? 0))}</Typography>
                                        </Grid>
                                    </Grid>
                                </>
                            );
                        } else {
                            return null;
                        }
                    })}
                    <ContentSeparator title="RELAÇÃO DE DESPESAS" />

                    <Grid sx={{ marginTop: '1rem' }}>
                        <Typography>VALOR TOTAL DAS DESPESAS</Typography>
                        <Grid sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }} data-clarity-mask="true">
                                {formatCurrency(Number(data.totalDespesas ?? 0))}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Box sx={{ width: '100%', marginTop: '2rem' }}>
                        <TableContainer sx={{ overflowX: 'auto' }} component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCellStyled align="left">Data</TableCellStyled>
                                        <TableCellStyled align="left">Descrição do Serviço</TableCellStyled>
                                        <TableCellStyled align="left">Beneficiário</TableCellStyled>
                                        <TableCellStyled align="left">Valor do Serviço</TableCellStyled>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.despesas.length > 0 ? (
                                        data.despesas.map((despesa, index) => (
                                            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap' }} align="left">
                                                    {formatDate(getDateFromSap(despesa.datemi), true)}
                                                </TableCell>
                                                <TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap' }} align="left">
                                                    {despesa.descricaoServico}
                                                </TableCell>
                                                <TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap' }} align="left">
                                                    {despesa.nome}
                                                </TableCell>
                                                <TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap' }} align="left">
                                                    {formatCurrency(Number(despesa.valorServico ?? 0))}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow key={0} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap' }} align="left">
                                                Não há despesas para exibir.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </>
            ) : (
                ''
            )}
        </Container>
    );
};
